import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@ui/components/ui/dialog';
import { Button } from '@ui/components/ui/button';
import { capitalize } from '@app/utils';
import { ApproveSprintFormValues, SprintProductEntity, SprintProductClientEntity } from '@data';
import React, { ReactElement, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { Skeleton } from '@ui/components/ui/skeleton';
import { type ApprovalRecipientsFieldHandle, type FormOptionType } from './ApprovalRecipientsField';
import { RecipientsSection, useDefaultRecipients } from './RecipientsSection';

type ValuesType = {
  recipients: FormOptionType[]; // the default set, minus anyone removed, plus free-typed external emails
};

export interface ApprovalPopupProps extends React.HTMLAttributes<HTMLDivElement> {
  sprintProduct: Partial<SprintProductClientEntity> | Partial<SprintProductEntity>;
  onClose: () => void;
  approveSprintProduct: (values: ApproveSprintFormValues) => void;
  sprintId: string;
  productId: string;
  isLoading?: boolean;
  isApproving: boolean;
  show?: boolean;
}

export const ApprovalPopup = ({
  sprintProduct,
  onClose,
  approveSprintProduct,
  productId,
  sprintId,
  isLoading,
  isApproving,
  show = true,
}: ApprovalPopupProps): ReactElement => {
  const { t } = useTranslation();

  const recipientsFieldRef = useRef<ApprovalRecipientsFieldHandle>(null);

  // Approving is no longer something you write a covering note on: the list of people the decision
  // reaches is the whole content of it.
  const recipients = useDefaultRecipients(sprintProduct?.id);

  const initialValues: ValuesType = useMemo(() => ({ recipients: recipients.initial }), [recipients.initial]);

  const { control, handleSubmit, reset, setError, getValues, watch } = useForm<ValuesType>({
    defaultValues: initialValues,
  });

  useEffect(() => {
    reset(initialValues);
  }, [initialValues, reset]);

  // Nobody left to write to: approving would go nowhere, so the button waits for at least one name.
  const noRecipients = watch('recipients').length === 0;

  const busy = isApproving || isLoading || recipients.isPending;

  const onApprove = (values: ValuesType) => {
    approveSprintProduct({
      productId,
      sprintId,
      sprintProductId: sprintProduct?.id,
      approvalRecipients: Array.from(new Set(values.recipients.map(x => x.value))),
    });
  };

  return (
    <Dialog open={show} onOpenChange={open => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {!isLoading ? (
              t('doYouWantToApprove', { name: capitalize(sprintProduct?.product?.name) })
            ) : (
              <Skeleton as="span" className="inline-block h-7 w-2/3" />
            )}
          </DialogTitle>
        </DialogHeader>
        <form
          onSubmit={handleSubmit(() => {
            // Flush a valid email still typed in the "To" box (handleSubmit captured the snapshot
            // before this ran), then re-read the form values so it isn't dropped.
            if (recipientsFieldRef.current && !recipientsFieldRef.current.commitDraft()) {
              return;
            }
            onApprove(getValues() as unknown as ValuesType);
          })}
        >
          <div className="mb-6">
            <p className="mb-4 text-sm text-muted-foreground">{t('forwardApprovalAlwaysHint')}</p>

            <Controller
              name="recipients"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <RecipientsSection
                  ref={recipientsFieldRef}
                  recipients={recipients}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  disabled={busy}
                  error={error}
                  onDraftInvalid={() => setError('recipients', { type: 'manual', message: t('invalidEmailDraft') })}
                />
              )}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
              {t('actions.cancel')}
            </Button>
            <Button type="submit" loading={isApproving} disabled={busy || noRecipients}>
              {t('actions.approveAndForward')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
